import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getContainers } from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { StatusBadge, TrafficLight, CostBadge, resolveTrafficLight } from './ui/StatusBadge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { formatDate } from '../lib/utils';
import {
  Plus, AlertTriangle, Package, Clock, Trash2, Edit2, Box, ChevronDown, ChevronRight, ChevronUp,
  Loader2, ArrowUpDown, ArrowUp, ArrowDown, ShieldCheck,
} from 'lucide-react';

// Sortable column header
function SortableHeader({ label, sortKey, sortConfig, onSort, title }) {
  const isActive = sortConfig.key === sortKey;
  const isAsc = isActive && sortConfig.direction === 'asc';
  const isDesc = isActive && sortConfig.direction === 'desc';

  const handleClick = () => {
    if (!isActive) onSort({ key: sortKey, direction: 'asc' });
    else if (isAsc) onSort({ key: sortKey, direction: 'desc' });
    else onSort({ key: 'default', direction: 'desc' });
  };

  return (
    <th
      className="py-2 px-3 font-medium cursor-pointer select-none hover:bg-secondary/50 transition-colors group"
      onClick={handleClick}
      title={title || `Ordenar por ${label}`}
      data-testid={`sort-header-${sortKey}`}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className={`transition-opacity ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`}>
          {isAsc ? <ArrowUp size={12} /> : isDesc ? <ArrowDown size={12} /> : <ArrowUpDown size={11} />}
        </span>
      </div>
    </th>
  );
}

// Expandable container subtable
function ContainerSubTable({ mbl, isOpen }) {
  const { t } = useI18n();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (isOpen && !loaded) {
      (async () => {
        setLoading(true);
        try {
          const res = await getContainers(mbl);
          setContainers(res.data);
          setLoaded(true);
        } catch (err) {
          console.error('Error loading containers:', err);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [isOpen, loaded, mbl]);

  if (!isOpen) return null;

  return (
    <tr>
      <td colSpan={10} className="p-0 bg-secondary/20">
        <div className="p-3 border-l-4 border-primary/50">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-2">
              <Loader2 size={14} className="animate-spin" />
              <span className="text-sm">{t('shipments.containerLoading')}</span>
            </div>
          ) : containers.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">
              {t('shipments.noContainersRegistered')}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border/50">
                  <th className="py-1.5 px-2 text-left font-medium">{t('shipments.containerNum')}</th>
                  <th className="py-1.5 px-2 text-left font-medium">{t('shipments.status')}</th>
                  <th className="py-1.5 px-2 text-left font-medium">{t('shipments.trafficLight')}</th>
                  <th className="py-1.5 px-2 text-left font-medium">{t('shipments.arrivalCol')}</th>
                  <th className="py-1.5 px-2 text-left font-medium">{t('shipments.deliveryCol')}</th>
                  <th className="py-1.5 px-2 text-left font-medium">{t('shipments.daysInPortCol')}</th>
                </tr>
              </thead>
              <tbody>
                {containers.map((container) => (
                  <tr key={container.id} className="border-b border-border/30 hover:bg-secondary/30">
                    <td className="py-1.5 px-2 font-mono text-xs">{container.container_number}</td>
                    <td className="py-1.5 px-2">
                      <StatusBadge code={container.status_container_code} label={container.status_label} />
                    </td>
                    <td className="py-1.5 px-2">
                      <TrafficLight status={resolveTrafficLight(container)} />
                    </td>
                    <td className="py-1.5 px-2 text-muted-foreground">{formatDate(container.arrived_at)}</td>
                    <td className="py-1.5 px-2 text-muted-foreground">{formatDate(container.delivered_at)}</td>
                    <td className="py-1.5 px-2">
                      {container.days_in_port_label ? (
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${
                          container.delivered_at
                            ? 'bg-green-500/20 text-green-400'
                            : container.days_in_port > 7
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          <Clock size={10} className="inline mr-1" />
                          {container.days_in_port_label}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </td>
    </tr>
  );
}

// Mobile card — optimized for quick scanning + tap-to-detail on phones
export function ShipmentCard({ shipment, showInternalColumns, isAdmin, onSelect, isSelected, onRevalidate, onEditMbl, hasField }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const etaReal = shipment.eta_real || shipment.eta_port;
  const estDelivery = shipment.eta_entrega_estimada || shipment.eta_final;
  const days = shipment.days_total;
  const isDelivered = !!shipment.delivered_at;

  return (
    <div
      className={`bg-card border border-border rounded-sm overflow-hidden ${isSelected ? 'ring-1 ring-primary' : ''}`}
      data-testid={`shipment-card-${shipment.mbl}`}
    >
      {/* Header: MBL + Status (full-width tap area to detail) */}
      <Link
        to={`/shipments/${encodeURIComponent(shipment.mbl)}`}
        className="block px-3 pt-3 pb-2 hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isAdmin && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => onSelect(shipment.mbl, checked)}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                className="mt-0.5 shrink-0"
              />
            )}
            <span className="font-mono text-base font-semibold text-primary truncate">
              {shipment.mbl}
            </span>
          </div>
          <StatusBadge status={shipment.status_general_code} label={shipment.status_general_label} size="sm" />
        </div>
        {showInternalColumns && shipment.client_name && (
          <p className="text-xs text-muted-foreground mt-1 truncate">{shipment.client_name}</p>
        )}
      </Link>

      {/* Key tracking info — always visible, scan-friendly */}
      <div className="px-3 py-2 border-t border-border/50 grid grid-cols-3 gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">ETA</div>
          <div className="text-sm font-medium truncate">{formatDate(etaReal) || '—'}</div>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('shipments.estDelivery')}</div>
          <div className={`text-sm font-medium truncate ${isDelivered ? 'text-emerald-400' : (estDelivery ? 'text-primary' : '')}`}>
            {isDelivered ? formatDate(shipment.delivered_at) : (formatDate(estDelivery) || '—')}
          </div>
        </div>
        <div className="min-w-0 flex flex-col items-end">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('shipments.days')}</div>
          <div className="flex items-center gap-1.5">
            {days !== undefined && days !== null ? (
              <span
                className={`text-xs font-mono ${shipment.days_status === 'cerrado' ? 'text-muted-foreground' : days > 7 ? 'text-red-400' : days > 3 ? 'text-amber-400' : 'text-emerald-400'}`}
                title={shipment.days_total_label}
              >
                {shipment.days_total_label || `${days}d`}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
            <Box size={12} className="text-muted-foreground ml-1" />
            <span className="text-xs font-medium">{shipment.container_count || 0}</span>
          </div>
        </div>
      </div>

      {/* Action row + expand toggle (44px touch targets) */}
      <div className="px-2 py-1.5 border-t border-border/50 flex items-center justify-between gap-1">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex-1 h-10 text-xs text-muted-foreground flex items-center justify-center gap-1 hover:text-foreground rounded-sm"
          data-testid={`expand-card-${shipment.mbl}`}
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          <span>{expanded ? t('shipments.collapse') : t('shipments.expand')}</span>
        </button>
        {isAdmin && onEditMbl && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEditMbl(shipment.mbl); }}
            className="h-10 w-10 flex items-center justify-center text-muted-foreground hover:text-primary rounded-sm hover:bg-secondary"
            title={t('shipments.editMbl') || 'Editar MBL'}
            data-testid={`edit-mbl-card-${shipment.mbl}`}
          >
            <Edit2 size={16} />
          </button>
        )}
        {isAdmin && onRevalidate && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRevalidate(shipment.mbl); }}
            className="h-10 w-10 flex items-center justify-center text-muted-foreground hover:text-blue-400 rounded-sm hover:bg-secondary"
            title={t('shipments.revalidateShipmentTitle')}
            data-testid={`revalidate-shipment-card-${shipment.mbl}`}
          >
            <ShieldCheck size={16} />
          </button>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 py-3 border-t border-border bg-secondary/20 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {showInternalColumns && (
            <>
              <div className="text-muted-foreground text-xs">{t('shipments.importer')}</div>
              <div className="text-xs truncate text-right">{shipment.importer_name || '—'}</div>
              <div className="text-muted-foreground text-xs">{t('shipments.cargoType')}</div>
              <div className="text-xs text-right">{shipment.cargo_type || '—'}</div>
            </>
          )}
          <div className="text-muted-foreground text-xs">{t('shipments.initialEta')}</div>
          <div className="text-xs text-right">{formatDate(shipment.eta_initial || shipment.eta_port) || '—'}</div>
          {!isDelivered && shipment.delivered_at === null && (
            <>
              <div className="text-muted-foreground text-xs">{t('shipments.realDelivery')}</div>
              <div className="text-xs text-right text-muted-foreground">—</div>
            </>
          )}
          {hasField && hasField('view_costs') && (
            <>
              <div className="text-muted-foreground text-xs">{t('shipments.cost')}</div>
              <div className="text-right"><CostBadge cost={shipment.total_cost} /></div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Desktop table
export function ShipmentsTable({
  shipments,
  loading,
  isClientView,
  showInternalColumns,
  isAdmin,
  hasField,
  selectedShipments,
  expandedRows,
  sortConfig,
  onSort,
  onSelect,
  onSelectAll,
  onToggleExpand,
  onRevalidate,
  onEditMbl,
  onDelete,
}) {
  const { t } = useI18n();

  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full dense-table">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
              {isAdmin && (
                <th className="py-2 px-2 w-8">
                  <Checkbox
                    checked={selectedShipments.length === shipments.length && shipments.length > 0}
                    onCheckedChange={onSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
              )}
              {showInternalColumns && <th className="py-2 px-2 font-medium w-8"></th>}
              <SortableHeader label="MBL/BL" sortKey="mbl" sortConfig={sortConfig} onSort={onSort} />
              {showInternalColumns && <SortableHeader label={t('shipments.client')} sortKey="client" sortConfig={sortConfig} onSort={onSort} />}
              {showInternalColumns && <SortableHeader label={t('shipments.importer')} sortKey="importer" sortConfig={sortConfig} onSort={onSort} />}
              {showInternalColumns && <th className="py-2 px-3 font-medium">{t('shipments.cargoType')}</th>}
              <th className="py-2 px-3 font-medium">{t('shipments.status')}</th>
              {showInternalColumns && <th className="py-2 px-2 font-medium text-center" title={t('shipments.docsHint')}>{t('shipments.docs')}</th>}
              <SortableHeader label={t('shipments.containers')} sortKey="containers" sortConfig={sortConfig} onSort={onSort} />
              <SortableHeader label={t('shipments.days')} sortKey="days" sortConfig={sortConfig} onSort={onSort} title={t('shipments.daysHint')} />
              <SortableHeader label={t('shipments.initialEta')} sortKey="eta_initial" sortConfig={sortConfig} onSort={onSort} title={t('shipments.initialEtaHint')} />
              <SortableHeader label={t('shipments.realEta')} sortKey="eta_real" sortConfig={sortConfig} onSort={onSort} title={t('shipments.realEtaHint')} />
              <SortableHeader label={t('shipments.estDelivery')} sortKey="eta_entrega" sortConfig={sortConfig} onSort={onSort} title={t('shipments.estDeliveryHint')} />
              <th className="py-2 px-3 font-medium" title={t('shipments.realDelivery')}>{t('shipments.realDelivery')}</th>
              {hasField('view_costs') && <th className="py-2 px-3 font-medium">{t('shipments.cost')}</th>}
              {showInternalColumns && <SortableHeader label={t('shipments.lastUpdate')} sortKey="updated" sortConfig={sortConfig} onSort={onSort} />}
              {isAdmin && <th className="py-2 px-2 font-medium w-16"></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={showInternalColumns ? (isAdmin ? 17 : 15) : 9} className="py-8 text-center text-muted-foreground">
                  {t('shipments.loadingShipments')}
                </td>
              </tr>
            ) : shipments.length === 0 ? (
              <tr>
                <td colSpan={showInternalColumns ? (isAdmin ? 17 : 15) : 9} className="py-8 text-center text-muted-foreground">
                  {isClientView ? t('shipments.noActiveForClient') : t('shipments.noShipmentsFound')}
                </td>
              </tr>
            ) : (
              shipments.map((shipment) => (
                <React.Fragment key={shipment.mbl}>
                  <tr
                    className={`border-b border-border/50 hover:bg-secondary/30 transition-colors ${selectedShipments.includes(shipment.mbl) ? 'bg-primary/10' : ''}`}
                    data-testid={`shipment-row-${shipment.mbl}`}
                  >
                    {isAdmin && (
                      <td className="py-2 px-2">
                        <Checkbox
                          checked={selectedShipments.includes(shipment.mbl)}
                          onCheckedChange={(checked) => onSelect(shipment.mbl, checked)}
                          data-testid={`select-${shipment.mbl}`}
                        />
                      </td>
                    )}
                    {showInternalColumns && (
                      <td className="py-2 px-2">
                        {shipment.container_count > 0 && (
                          <button
                            onClick={() => onToggleExpand(shipment.mbl)}
                            className="p-1 hover:bg-secondary rounded-sm transition-colors"
                            data-testid={`expand-btn-${shipment.mbl}`}
                          >
                            {expandedRows[shipment.mbl] ? (
                              <ChevronDown size={14} className="text-primary" />
                            ) : (
                              <ChevronRight size={14} className="text-muted-foreground" />
                            )}
                          </button>
                        )}
                      </td>
                    )}
                    <td className="py-2 px-3">
                      <Link
                        to={`/shipments/${encodeURIComponent(shipment.mbl)}`}
                        className="font-mono text-sm text-primary hover:underline"
                      >
                        {shipment.mbl}
                      </Link>
                      {!shipment.is_active && showInternalColumns && (
                        <span className="ml-2 text-xs text-muted-foreground">{t('shipments.inactiveTag')}</span>
                      )}
                    </td>
                    {showInternalColumns && (
                      <td className="py-2 px-3 text-sm">{shipment.client_name || '—'}</td>
                    )}
                    {showInternalColumns && (
                      <td className="py-2 px-3 text-sm text-muted-foreground">{shipment.importer_name || '—'}</td>
                    )}
                    {showInternalColumns && (
                      <td className="py-2 px-3 text-sm text-muted-foreground">{shipment.cargo_type || '—'}</td>
                    )}
                    <td className="py-2 px-3">
                      <StatusBadge code={shipment.status_general_code} label={shipment.status_label} />
                    </td>
                    {showInternalColumns && (
                      <td className="py-2 px-2 text-center">
                        {shipment.docs_sent_at ? (
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 bg-green-500/20 text-green-400 rounded-full text-xs"
                            title={`${t('shipments.sentOn')}: ${formatDate(shipment.docs_sent_at)}`}
                          >
                            ✓
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 bg-amber-500/20 text-amber-400 rounded-full text-xs"
                            title={t('shipments.docsPending')}
                          >
                            !
                          </span>
                        )}
                      </td>
                    )}
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1.5">
                        <Package size={14} className="text-muted-foreground" />
                        <span className="font-mono text-sm">{shipment.container_count}</span>
                        {shipment.container_count === 0 && showInternalColumns && (
                          <AlertTriangle size={14} className="text-amber-400" title={t('shipments.noContainersFlag')} />
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      {shipment.days_total !== null && shipment.days_total !== undefined ? (
                        <div
                          className={`flex items-center gap-1 text-sm ${shipment.days_status === 'cerrado' ? 'text-muted-foreground' : 'text-amber-400'}`}
                          title={shipment.days_total_label}
                        >
                          <Clock size={12} />
                          <span className="font-mono">{shipment.days_total_label}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-sm text-muted-foreground" title={t('shipments.initialEtaHint')}>
                      {formatDate(shipment.eta_port)}
                    </td>
                    <td className="py-2 px-3 text-sm">
                      {shipment.eta_real ? (
                        <span className="text-green-400">{formatDate(shipment.eta_real)}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-sm">
                      {shipment.eta_entrega_estimada ? (
                        <span className="text-primary" title={t('shipments.autoCalculated')}>
                          {formatDate(shipment.eta_entrega_estimada)}
                        </span>
                      ) : shipment.eta_final ? (
                        <span className="text-muted-foreground">{formatDate(shipment.eta_final)}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-sm">
                      {shipment.delivered_at ? (
                        <span className="text-green-400">{formatDate(shipment.delivered_at)}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    {hasField('view_costs') && (
                      <td className="py-2 px-3">
                        <CostBadge calculated={shipment.cost_calculated} cost={shipment.total_cost} />
                      </td>
                    )}
                    {showInternalColumns && (
                      <td className="py-2 px-3 text-sm text-muted-foreground">
                        {formatDate(shipment.last_update_at)}
                      </td>
                    )}
                    {isAdmin && (
                      <td className="py-2 px-2 flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onRevalidate(shipment.mbl)}
                          className="h-7 w-7 text-muted-foreground hover:text-blue-400"
                          title={t('shipments.revalidateShipmentTitle')}
                          data-testid={`revalidate-shipment-${shipment.mbl}`}
                        >
                          <ShieldCheck size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditMbl(shipment)}
                          className="h-7 w-7 text-muted-foreground hover:text-primary"
                          title={t('shipments.editMblBtn')}
                          data-testid={`edit-mbl-${shipment.mbl}`}
                        >
                          <Edit2 size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(shipment)}
                          className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          title={t('shipments.deleteTitle')}
                          data-testid={`delete-btn-${shipment.mbl}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    )}
                  </tr>
                  {showInternalColumns && (
                    <ContainerSubTable mbl={shipment.mbl} isOpen={expandedRows[shipment.mbl]} />
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
